/**
 * Configured log sources.
 *
 * The list doubles as the estate's cost sheet: what each source keeps, what that costs, and what
 * the platform thinks is wrong with it. A source with warnings is still usable — some warnings
 * are deliberate choices — but it is never possible to have one without having seen them.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, type SourceSummary } from '../api'
import { useSession } from '../App'
import { Async, Card, Chip, Empty, Modal, Mono, Note, fmtBytes, useAsync, useToast } from '../components/ui'

export default function Sources() {
  const navigate = useNavigate()
  const { can, me } = useSession()
  const toast = useToast()
  const sources = useAsync<SourceSummary[]>(() => api.get<SourceSummary[]>('/sources'), [me.tenant])
  const [sending, setSending] = useState<SourceSummary | null>(null)

  function toggle(s: SourceSummary) {
    api
      .put('/sources', { ...s, enabled: !s.enabled })
      .then(() => {
        toast('ok', `${s.display_name} ${s.enabled ? 'disabled' : 'enabled'}`)
        sources.reload()
      })
      .catch((e: Error) => toast('error', e.message))
  }

  function remove(s: SourceSummary) {
    if (!window.confirm(`Delete "${s.display_name}"? Events already ingested are kept; nothing new will be accepted for it.`)) return
    api
      .del(`/sources/${s.id}`)
      .then(() => {
        toast('ok', 'Source deleted')
        sources.reload()
      })
      .catch((e: Error) => toast('error', e.message))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Log sources</h1>
          <div className="sub">
            Each source carries the field policy agreed at onboarding: what is kept, what is
            dropped, and what that costs. Editing a policy bumps its version, so events record
            which policy parsed them.
          </div>
        </div>
        <div className="actions">
          {can('configure_sources') && (
            <button className="primary" onClick={() => navigate('/sources/new')}>
              Onboard a source
            </button>
          )}
        </div>
      </div>

      <Async
        state={sources}
        empty={
          <Card>
            <Empty
              title="No log sources yet"
              action={
                can('configure_sources') ? (
                  <button className="primary" onClick={() => navigate('/sources/new')}>
                    Onboard your first source
                  </button>
                ) : null
              }
            >
              Bastion accepts nothing until a source is configured. That is deliberate: an
              unconfigured source has no field policy, so there is no answer to what should be
              kept.
            </Empty>
          </Card>
        }
      >
        {(list) => (
          <div className="stack">
            {list.map((s) => {
              const kept = s.field_rules.filter((r) => r.disposition !== 'drop').length
              const keyed = s.field_rules.filter((r) => r.observable).length
              return (
                <Card
                  key={s.id}
                  title={
                    <span className="row" style={{ gap: 8 }}>
                      {s.display_name}
                      {!s.enabled && <Chip tone="neutral">disabled</Chip>}
                      {s.warnings.length > 0 && <Chip tone="warn">{s.warnings.length} warning(s)</Chip>}
                    </span>
                  }
                  subtitle={
                    <>
                      <Mono>{s.id}</Mono> · {s.format} · v{s.version}
                      {s.vendor && <> · {s.vendor}</>}
                    </>
                  }
                  actions={
                    <>
                      {can('configure_sources') && (
                        <button className="sm" onClick={() => setSending(s)}>
                          Send test data
                        </button>
                      )}
                      {can('configure_sources') && (
                        <button className="sm" onClick={() => toggle(s)}>
                          {s.enabled ? 'Disable' : 'Enable'}
                        </button>
                      )}
                      {can('configure_sources') && (
                        <button className="sm danger" onClick={() => remove(s)}>
                          Delete
                        </button>
                      )}
                    </>
                  }
                >
                  {s.warnings.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <Note tone="warn">
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {s.warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      </Note>
                    </div>
                  )}

                  <div className="grid cols-4">
                    <div>
                      <div className="label small faint">Fields kept</div>
                      <div className="tnum" style={{ fontSize: 18 }}>
                        {kept} <span className="faint small">of {s.field_rules.length}</span>
                      </div>
                    </div>
                    <div>
                      <div className="label small faint">Entity fields</div>
                      <div className="tnum" style={{ fontSize: 18 }}>
                        {keyed}
                      </div>
                    </div>
                    <div>
                      <div className="label small faint">Retention</div>
                      <div style={{ fontSize: 18 }}>
                        {s.retention.hot_days}d hot
                        {s.retention.keep_raw && <span className="small muted"> + {s.retention.raw_days}d raw</span>}
                      </div>
                    </div>
                    <div>
                      <div className="label small faint">Estimated storage</div>
                      <div className="tnum" style={{ fontSize: 18 }}>
                        {fmtBytes(s.estimated_daily_bytes)}
                        <span className="faint small">/day at 1M events</span>
                      </div>
                    </div>
                  </div>

                  <details style={{ marginTop: 12 }}>
                    <summary className="small muted" style={{ cursor: 'pointer' }}>
                      Field policy ({s.field_rules.length} rules)
                    </summary>
                    <div className="table-wrap" style={{ marginTop: 8 }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Handling</th>
                            <th>Stored as</th>
                            <th>Entity</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.field_rules.map((r) => (
                            <tr key={r.source_field}>
                              <td><Mono>{r.source_field}</Mono></td>
                              <td>
                                <Chip
                                  tone={
                                    r.disposition === 'drop'
                                      ? 'neutral'
                                      : r.disposition === 'redact'
                                        ? 'danger'
                                        : r.disposition === 'key_field'
                                          ? 'ok'
                                          : 'outline'
                                  }
                                >
                                  {r.disposition.replace(/_/g, ' ')}
                                </Chip>
                              </td>
                              <td className="mono small">{r.rename_to ?? r.source_field}</td>
                              <td className="small">
                                {r.observable ? (
                                  <Chip tone="outline">
                                    {r.observable}
                                    {r.observable_role ? `:${r.observable_role}` : ''}
                                  </Chip>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="small muted">{r.rationale || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </Card>
              )
            })}
          </div>
        )}
      </Async>

      {sending && <SendData source={sending} onClose={() => setSending(null)} onDone={sources.reload} />}
    </>
  )
}

function SendData({
  source,
  onClose,
  onDone,
}: {
  source: SourceSummary
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{
    accepted: number
    sampled_away: number
    signals: number
    incidents_opened: string[]
    runs_started: string[]
    warnings: string[]
  } | null>(null)

  function send() {
    setBusy(true)
    api
      .post<typeof result>('/ingest', { source: source.id, lines: text.split('\n') })
      .then((r) => {
        setResult(r)
        onDone()
        if (r) toast('ok', `${r.accepted} event(s) ingested, ${r.signals} signal(s) raised`)
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      title={`Send data to ${source.display_name}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Close</button>
          <button className="primary" disabled={busy || !text.trim()} onClick={send}>
            {busy ? 'Sending…' : 'Send through the pipeline'}
          </button>
        </>
      }
    >
      <p className="small muted">
        These lines go through the whole pipeline — parse, enrich, detect, correlate — exactly as
        live data would. Useful for checking that a rule you just wrote actually fires.
      </p>
      <textarea rows={10} placeholder="One log line per line…" value={text} onChange={(e) => setText(e.target.value)} />
      {result && (
        <div style={{ marginTop: 12 }}>
          <dl className="kv">
            <dt>Accepted</dt>
            <dd className="tnum">{result.accepted}</dd>
            <dt>Sampled away</dt>
            <dd className="tnum">{result.sampled_away}</dd>
            <dt>Signals raised</dt>
            <dd className="tnum">{result.signals}</dd>
            <dt>Incidents opened</dt>
            <dd className="mono small">{result.incidents_opened.join(', ') || 'none'}</dd>
            <dt>Playbook runs</dt>
            <dd className="mono small">{result.runs_started.join(', ') || 'none'}</dd>
          </dl>
          {result.warnings.length > 0 && (
            <Note tone="warn">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Note>
          )}
        </div>
      )}
    </Modal>
  )
}
